import api from '../plugins/axios';
import { LOCAL_STORAGE_KEY } from '@/utils/constants';
import router from '@/router';

function handleError(error) {
  console.error('API Error:', error);
}
const TIMEOUT = 300000; // 5 min

export default {
  state: {
    currentUser: {},
    token: null,
    temporaryToken: null,
    sig: null,
    email: null,
    workspaces: [],
    currentWorkspace: {},
    settings: {},
    permissions: null,
    values: [],
    checklist: {},
    isLogin: false,
    isLoggedIn: false,
  },
  mutations: {
    setLoggedOut(state) {
      state.currentUser = {};
      state.token = null;
      state.currentWorkspace = {};
      state.settings = {};
      state.permissions = null;
      state.values = [];
      state.checklist = {};
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      state.isLogin = false;
      state.isLoggedIn = false;
    },
    setTemporaryToken(state, token) {
      state.temporaryToken = token;
    },
    setWorkspaces(state, workspaces) {
      state.workspaces = workspaces;
    },
    setCurrentWorkspace(state, workspace) {
      state.currentWorkspace = workspace;
    },
    setCurrentUser(state, user) {
      Object.assign(state.currentUser, user);
    },
    setIsLoggedIn(state, isLoggedIn) {
      state.isLoggedIn = isLoggedIn;
    },
    setAppConfig(state, data) {
      state.currentUser = data.user;
      state.token = data.access_token;
      state.currentWorkspace = data.workspace;
      state.settings = data.settings;
      state.permissions = data.permissions;
      state.checklist = data.checklist;
      state.isLogin = true;
      state.isLoggedIn = true;
    },
    setScoreRemaining(state, data) {
      state.currentUser.score_remaining = data;
    },
    updateCurrentWorkspace(state, data) {
      Object.assign(state.currentWorkspace, data);
    },
    setCurrentSettings(state, data) {
      state.settings = data;
    },
    setValues(state, values) {
      state.values = values;
    },
    setChecklist(state, checklist) {
      state.checklist = checklist;
    },
    addValue(state, value) {
      if (value.default) {
        const prevDefaultValue = state.values.find((el) => el.default);
        if (prevDefaultValue) Object.assign(prevDefaultValue, { default: false });
      }
      state.values.push(value);
    },
    updateValue(state, value) {
      const oldCurrentValue = state.values.find((el) => el.id === value.id);
      if (oldCurrentValue) {
        if (value.default) {
          const prevDefaultValue = state.values.find((el) => el.default);
          if (prevDefaultValue.id !== oldCurrentValue.id) Object.assign(prevDefaultValue, { default: false });
        }
        Object.assign(oldCurrentValue, value);
      }
    },
    deleteValue(state, value) {
      const index = state.values.findIndex((el) => el.id === value.id);
      if (index > -1) {
        state.values.splice(index, 1);
      }
    },
    setEmail(state, email) {
      state.email = email;
    },
  },
  actions: {
    async init({ commit, dispatch }) {
      try {
        let isLoggedIn = false;
        const currentRoute = router.currentRoute.value;
        try {
          isLoggedIn = !!localStorage.getItem(LOCAL_STORAGE_KEY);
        } catch (e) {
          console.error('Error accessing localStorage:', e);
        }
        commit('setIsLoggedIn', isLoggedIn);

        if (isLoggedIn && currentRoute.path !== '/goodbye') {
          await dispatch('getWorkspace');
          await dispatch('getConfig');
        }
      } catch (e) {
        commit('setLoggedOut');
        handleError(e);
      }
    },
    async getCode(_, data) {
      try {
        const resp = await api.post('/auth/get_code', { email: data.email });
        localStorage.setItem('karma_sig', resp.data.sig);
        localStorage.setItem('karma_email', data.email);
      } catch (e) {
        handleError(e);
      }
    },
    async verifyCode({ commit }, code) {
      const sig = localStorage.getItem('karma_sig');
      const email = localStorage.getItem('karma_email');
      const resp = await api.post('/auth/verify_code', { email, sig, code });
      localStorage.removeItem('karma_sig');
      localStorage.removeItem('karma_email');
      commit('setEmail', email);
      commit('setWorkspaces', resp.data.workspaces);
      commit('setTemporaryToken', resp.data.access_token);
    },
    async verifyInvitationToken({ commit }, token) {
      await api.post('/auth/session/verify_invite', token);
      commit('setTemporaryToken', token.token);
    },
    async acceptInvite({ commit, dispatch }, token) {
      try {
        const resp = await api.post('/auth/session/accept_invite', token);
        localStorage.setItem(LOCAL_STORAGE_KEY, resp.data.access_token);
        commit('setTemporaryToken', null);
        await dispatch('getConfig');
        await dispatch('getWorkspace');
      } catch (e) {
        handleError(e);
      }
    },
    async verifyGoogleIDToken({ commit }, idToken) {
      try {
        const resp = await api.post('/auth/verify_google_id_token', { id_token: idToken });
        localStorage.removeItem('karma_sig');
        localStorage.removeItem('karma_email');
        commit('setEmail', resp.data.email);
        commit('setWorkspaces', resp.data.workspaces);
        commit('setTemporaryToken', resp.data.access_token);
      } catch (e) {
        handleError(e);
      }
    },
    async createWorkspace({ state, commit, dispatch }, workspaceData) {
      try {
        const resp = await api.post('/auth/workspaces', workspaceData, {
          headers: { Authorization: `Bearer ${state.temporaryToken}` },
        });
        localStorage.setItem(LOCAL_STORAGE_KEY, resp.data.access_token);
        localStorage.setItem(`needShowCreateDemo_${resp.data.workspace.id}`, 'true');
        commit('setTemporaryToken', null);
        commit('setWorkspaces', [...state.workspaces, resp.data.workspace]);
        await dispatch('getConfig');
      } catch (e) {
        handleError(e);
      }
    },
    async getWorkspace({ commit }) {
      try {
        const resp = await api.get('/auth/workspaces');
        commit('setWorkspaces', resp.data);
      } catch (e) {
        handleError(e);
      }
    },
    async getConfig({ commit, dispatch }) {
      try {
        const resp = await api.get('/config');
        commit('setAppConfig', {
          user: resp.data.user,
          access_token: resp.data.access_token,
          workspace: resp.data.workspace,
          settings: resp.data.settings,
          permissions: resp.data.permissions,
          checklist: resp.data.checklist,
        });
        commit('setValues', resp.data.values);
        await dispatch('settings/loadUsers', {}, { root: true });
        await dispatch('shop/loadRewards', null, { root: true });

        setTimeout(() => dispatch('getConfig'), TIMEOUT); // reload config every 5 minutes
      } catch (e) {
        handleError(e);
      }
    },
    async createSession({ state, commit, dispatch }, workspaceId) {
      commit('dashboard/resetData', {}, { root: true });
      try {
        const resp = await api.post(
          '/auth/session',
          { workspace_id: workspaceId },
          {
            headers: { Authorization: `Bearer ${state.temporaryToken}` },
          },
        );
        localStorage.setItem(LOCAL_STORAGE_KEY, resp.data.access_token);
        commit('setTemporaryToken', null);
        await dispatch('getConfig');
        await dispatch('feed/init', {}, { root: true });
      } catch (e) {
        handleError(e);
      }
    },
    async logout({ commit }) {
      commit('setLoggedOut');
    },
    async getCurrentUser({ commit }) {
      try {
        const resp = await api.get('/profiles/me');
        commit('setCurrentUser', resp.data);
      } catch (e) {
        handleError(e);
      }
    },
    async loadValues({ commit }) {
      try {
        const { data } = await api.get('/values');
        commit('setValues', data);
      } catch (e) {
        handleError(e);
        throw e;
      }
    },
    updateScoreValue({ commit, state }, data) {
      const leftScore = state.currentUser.score_remaining - data.score * data.to_id.length;
      commit('setScoreRemaining', leftScore);
    },
    async impersonate({ dispatch }, { token }) {
      const { data } = await api.post('/auth/session/impersonate/', { token });
      localStorage.setItem(LOCAL_STORAGE_KEY, data.access_token);
      await dispatch('getConfig');
      await dispatch('feed/init', {}, { root: true });
    },
    removeDemo() {
      return api.delete('/workspace/reset_demo_data');
    },
  },
  getters: {
    values(state) {
      return state.values;
    },
    limitPeriod(state) {
      return state.settings.limit_period || 'month';
    },
  },
  namespaced: true,
};
